import { LayoutTypeExport } from "@/types/LayoutType";
import { OptionsTypeExport } from "@/types/OptionType";
import { PageTypeError } from "@/types/PageType";
import { GlossarDataType } from "@/utils/getData/getGlossarData";
import { Post } from "@/wp/types/client";

export type LocaleTypes = "de_DE" | "en_US";
export type MediaDataType = {
  type: "image" | "video" | "svg" | "animation";
  image_mobile?: ImageType | false;
  image_tablet?: ImageType | false;
  image_desktop: ImageType | false;
  image_fullhd?: ImageType | false;
  video?: MediaVideoType;
  svg?: ImageType | false;
  svg_animation?: FileType | false;
  show_caption?: boolean;
  caption_position?: "has-text-left" | "has-text-right" | "has-text-centered";
  caption?: string;
};

export type MediaVideoType = {
  video_type: "Youtube" | "Vimeo" | "iframe" | "upload";
  externes_video?: string | null;
  externes_video_iframe?: string | null;
  internes_video?: FileType | false;
  video_thumbnail: ImageType | false;
  autoplay: boolean;
  audio?: boolean;
};

export type MediaVideoTypeVideoBreakpoints = MediaVideoType & {
  internes_video_desktop: FileType | false;
  internes_video_tablet: FileType | false;
  internes_video_mobile: FileType | false;
  captions_desktop: FileType | false;
  captions_tablet: FileType | false;
  captions_mobile: FileType | false;
};

export type FileType = {
  id: string;
  title: string;
  filename: string;
  url: string;
  link: string;
  alt: string;
  author: string;
  description: string;
  acf?: {
    text_1: "";
    link_1: "";
    text_2: "";
    link_2: "";
    decorative: boolean;
    has_transcript: boolean;
    transcript: string;
    captions: {
      url: string; //actually FileType but to prevent circular reference
    };
  };
};

export type ImageType = {
  id: string;
  title: string;
  filename: string;
  url: string;
  link: string;
  alt: string;
  author?: string;
  name: string;
  description: string;
  sizes?: {
    "Preview Icons": string;
    "Preview Icons-height": number;
    "Preview Icons-width": number;
    "Preview Images": string;
    "Preview Images-height": number;
    "Preview Images-width": number;
    large: string;
    "large-height": number;
    "large-width": number;
    medium: string;
    "medium-height": number;
    "medium-width": number;
    medium_large: string;
    "medium_large-height": number;
    "medium_large-width": number;
    thumbnail: string;
    "thumbnail-height": number;
    "thumbnail-width": number;
  };
  date?: string;
  modified?: string;
  mimetype?:
    | "image/jpeg"
    | "image/jpg"
    | "image/webp"
    | "image/png"
    | "image/svg+xml";
  mime_type?:
    | "image/jpeg"
    | "image/jpg"
    | "image/webp"
    | "image/png"
    | "image/svg+xml";
  image_blur: string;
  type?: "image";
  width: number;
  height: number;
  acf: {
    link_1: string;
    link_2: string;
    text_1: string;
    text_2: string;
  };
};

export function isAvailable<T>(data: unknown): data is T {
  return <boolean>data && typeof data !== "boolean";
}

export type MenuType = {
  term_id: number;
  name: string;
  slug: string;
  taxonomy: "nav_menu";
  parent: number;
  count: number;
  items: MenuItemType[];
};

export type MenuItemType = {
  ID?: number;
  menu_order: number;
  type?: string;
  type_label?: string;
  guid?: string;
  url: string;
  title: string;
  slug?: string | null;
  target: string;
  classes: string[];
  post_name: string;
  object_id?: string;
  child_items?: MenuItemType[] | null;
};

export type LinkType =
  | {
      title: string;
      url: string;
      target: string;
      type?: string;
      aria_label: string;
    }
  | null
  | ""
  | false;

export type PlatformsType = {
  name?: string;
  link?: string;
  icon?: ImageType;
  show_navbar?: boolean;
  show_footer?: boolean;
}[];

export type PlatformType = PlatformsType | false;

export type OptionsType = OptionsTypeExport;

export type YoastHeadType = {
  title: string;
  description: string;
  robots: {
    index: string;
    follow: string;
    "max-snippet": string;
    "max-image-preview": string;
    "max-video-preview": string;
  };
  canonical?: string;
  og_image: [{ height: number; type: string; url: string; width: number }];
  og_locale: string;
  og_type: string;
  og_title: string;
  og_description: string;
  og_url: string;
  og_site_name: string;
  article_modified_time: string;
  twitter_card: string;
  schema: unknown;
};

export type CategoryType = {
  id: number;
  count: number;
  link: string;
  name: string;
  slug: string;
  taxonomy: string;
  parent: number;
  acf: {
    link: string;
    image: ImageType;
    id: string;
  };
  yoast_head_json: YoastHeadType;
};

export type PostIdType = {
  ID: number;
  post_author: string;
  post_date: string;
  post_date_gmt: string;
  post_content: string;
  post_title: string;
  post_excerpt: string;
  post_status: "publish" | "draft" | "private";
  post_modified: string;
  post_modified_gmt: string;
  parent: number;
  guid: string;
  menu_order: number;
  post_type: string;
  post_mime_type: string;
};

export type PageTypeResponse = PageTypeError | Post;
export type WindowWithAnaylitcs = Window & {
  gtag?: unknown;
  gaData?: unknown;
  _paq?: unknown;
};

export type WPML_TranslationType = {
  locale: LocaleTypes;
  id: number;
  post_title: string;
  href: string;
};

export type LayoutType = LayoutTypeExport;

export type HeadlineData = {
  headline: string;
  ort: string;
  excerpt: string;
};

export type AccordionData = {
  headline: string;
  content: string;
};

export type NextJSStaticPathType = {
  params: { dynamicRoute: string[] };
  locale?: string;
};
export interface SearchType {
  posts: Post[] | null;
  keyword: string | null;
  postsTotal: number;
}

export type ColumnsType = {
  breakpoint:
    | "mobile"
    | "tablet"
    | "desktop"
    | "widescreen"
    | "fullhd"
    | "ultrahd"
    | false;
  number_of_cols: number;
  column_offset?: boolean; // for textmedia modul
};

export type IndexedModuleData<T> = {
  index: number;
  data: T;
}[];

export type ModuleData<
  IndexModuleDataType,
  T extends IndexedModuleData<IndexModuleDataType>,
> = T[number]["data"] | undefined;

export type UserType = {
  id?: number;
  profilePicture?: string | null; // Assuming profile picture could be a URL string
  firstName: string;
  lastName: string;
  email: string;
  isVerified?: boolean;
  favouriteFood?: string;
  userLocale?: string | null;
  locale?: string | null;
  role?: "user" | "admin" | "superadmin";
};

export interface EquipmentType {
  utensilien_id: number;
  utensilien_titel: string;
  utensilien_svg: string;
  utensilien_svg_width: number;
  utensilien_svg_height: number;
  utensilien_img: string;
  utensilien_img_width: number;
  utensilien_img_height: number;
}

export interface StepZutat {
  zutat_id: number;
  zutat_namen: string;
  zutat_rezeptverweis_id: number | null; // Can be a number or null
  step_zutat_menge: number;
  step_zutat_masseinheit: string;
  step_zutat_masseinheit_abkuerzung: string;
  step_zutat_freitext: string;
}

export interface StepZutatList extends RecipeType {
  zutaten_portion_big: StepZutat[];
  zutaten_portion_small: StepZutat[];
}

export interface RecipeStep {
  step_titel: string;
  step_beschreibung: string;
  step_kategorie: string;
  step_illustration: string; // URL to the image
  step_illustration_width: number; // Width of the illustration
  step_illustration_height: number; // Height of the illustration
  step_zutaten: StepZutat[];
}

// --- Main Recipe Interface ---

export type RecipeType = {
  rezept_id: number;
  rezept_titel: string;
  rezept_beschreibung: string;
  rezept_tipp: string;
  rezept_vegitipp: string;
  free_recipe: boolean;
  anzahl_portionen_small: number; // Can be an empty string, consider `string | null` if it truly means absence
  anzahl_portionen_big: number; // Same as above
  mengenrechner: boolean;
  portionsgroesse: string;
  portionsgroesse_min: number;
  portionsgroesse_max: number;
  mengenintervall: number;
  zubereitungszeit: number;
  zeit_backen: number;
  zeit_kuehlen: number;
  zeit_tiefkuehlen: number;
  zeit_ruhen: number;
  zeit_kochen: number;
  hauptkategorie_id: number;
  kategorien: number[]; // Array of numbers (IDs)
  garmethoden: number[]; // Array of numbers (IDs)
  unvertraeglichkeiten: string; // Can be an empty string, consider `string | null`
  schwierigkeitsgrad: number;
  produkttyp: "Der kleine Tiptopf" | "Tiptopf";
  topfi_tipp: string;
  saisons: number[]; // Array of numbers (IDs)
  ernaehrungsformen: number[]; // Array of numbers (IDs)
  geraetschaften: (number | false)[]; // Array of numbers (IDs)
  bild_desktop: string; // URL
  bild_tablet: string; // URL
  bild_mobile: string; // URL
  bild_pdf: string; // URL
  bild_titel: string;
  bild_alttext: string;
  bewertung_stars: number;
  bewertung_anzahl: number;
  steps_portion_big: RecipeStep[];
  steps_portion_small: RecipeStep[];
  base64: string; // Base64 encoded string for the PDF
  link: string; // URL to the recipe page
  umluft: boolean;
};

// export type RecipeType = {
//   rezept_id: number;
//   rezept_titel: string;
//   schwierigkeitsgrad: number;
//   produktyp: "Der kleine Tiptopf" | "Tiptopf";
// };
export type GlossarResponseType = {
  posts: Post<"glossary">[];
  filteredChar: string;
  postsTotal: number;
  filterChars: {
    [key: string]: string;
  };
};
export type GlossarReturnedType = {
  posts: GlossarDataType[];
  postsTotal: number;
  filterChars: {
    [key: string]: string;
  };
};

export type StepStage =
  | "intro"
  | "ingredients"
  | "equipment"
  | "step"
  | "outro";
